/**
 * @format
 */
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { FudisValidators } from '@funidata/ngx-fudis';
import { NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@ngneat/transloco';
import { MaxLength } from 'common-typescript/constants';
import { OtmId, Plan } from 'common-typescript/types';
import { Observable, take, tap } from 'rxjs';
import { ModalService, mono, requestQueueIgnoreConcurrent } from 'sis-common';
import {
    AlertsService,
    AlertType,
    AppErrorHandler,
    getValidationErrorSummaryDetail,
    handleSubmission,
    PlanEntityService,
    SisFormBuilder,
    ValidationErrorSummaryDetail,
} from 'sis-components';

export interface EditPlanNgNameModalValues {
    planId: OtmId;
}

interface EditPlanNameForm {
    name: FormControl<string>;
}

export function editPlanNameModalOpener(): (planId: OtmId) => NgbModalRef {
    const modalService: ModalService = inject(ModalService);
    return (planId: OtmId) => modalService.open(EditPlanNgNameModalComponent, { planId }, { size: 'sm' });
}

@Component({
    selector: 'app-edit-plan-ng-name-modal',
    templateUrl: './edit-plan-ng-name-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EditPlanNgNameModalComponent implements OnInit {
    planId: OtmId;
    plan: Plan;
    planNameForm: FormGroup<EditPlanNameForm>;
    validationDetails: ValidationErrorSummaryDetail[] = [];
    planObs$: Observable<Plan>;

    constructor(
        @Inject(ModalService.injectionToken) private values: EditPlanNgNameModalValues,
        public activeModal: NgbActiveModal,
        private planEntityService: PlanEntityService,
        private fb: SisFormBuilder,
        private appErrorHandler: AppErrorHandler,
        private alertsService: AlertsService,
        private translocoService: TranslocoService,
        private destroyedRef: DestroyRef,
    ) {
        this.planId = values.planId;
    }

    initPlanNameForm(): void {
        this.planNameForm = this.fb.group({
            name: [
                '',
                [
                    FudisValidators.minLength(
                        2,
                        this.translocoService.translate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.MIN_LENGTH', { minLength: 2 }),
                    ),
                    FudisValidators.maxLength(
                        MaxLength.MAX_TERSE_STRING_LENGTH,
                        this.translocoService.translate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.MAX_LENGTH', {
                            maxLength: MaxLength.MAX_TERSE_STRING_LENGTH,
                        }),
                    ),
                    FudisValidators.required(this.translocoService.translate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.REQUIRED')),
                ],
            ],
        });
    }

    ngOnInit(): void {
        this.planObs$ = this.planEntityService.getById(this.planId).pipe(
            take(1),
            tap((plan: Plan) => {
                this.initPlanNameForm();
                this.plan = plan;
                this.name.setValue(this.plan.name);
            }),
        );
    }

    private readonly save$ = requestQueueIgnoreConcurrent<boolean, Plan>(
        () => mono(this.save()),
        updatedPlan => this.activeModal.close(updatedPlan),
        this.appErrorHandler.defaultErrorHandler(),
        takeUntilDestroyed(this.destroyedRef),
    );

    private save(): Observable<Plan> {
        this.plan.name = this.name.value.trim();
        return this.planEntityService.updateMyPlan(this.plan).pipe(
            take(1),
            tap(() => {
                this.alertsService.addTemporaryAlert({
                    type: AlertType.SUCCESS,
                    message: this.translocoService.translate('PLAN.EDIT_PLAN_NAME_MODAL.PLAN_NAME_EDIT_SUCCESS'),
                });
            }),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    get name(): FormControl<string> {
        return this.planNameForm.controls.name;
    }

    onSubmit() {
        if (this.planNameForm.pristine) {
            this.activeModal.close();
            return;
        }
        if (!this.planNameForm.valid) {
            this.validationDetails = [
                getValidationErrorSummaryDetail(
                    this.name,
                    this.translocoService.translate('PLAN.EDIT_PLAN_NAME_MODAL.VALIDATION.ERROR_NAME_INPUT'),
                    'plan-name-edit-modal-name-input-id',
                ),
            ];
            return;
        }
        handleSubmission(this.planNameForm, this.save$);
    }
}
